import { Plan } from '@/features/subscription/types/plan';
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { planService } from '@/features/subscription/services/planService';
import { PlanStatusEnum } from '@/features/subscription/enum/planStatus';
import { PlanDetails } from '@/features/subscription/types/paddle';
import { User } from '@/types/models/user';
import { paymentService } from '@/features/subscription/services/paymentService';
import { transformSubscriptionToPlanDetails } from '@/features/subscription/utils/paddle/PaddleTypeTransformer';

// State type
interface PlanState {
  plan: Plan | null;
  planDetails: PlanDetails | null;
  planMembers: {
    user: User;
    role: string;
  }[];
  isActive: boolean;
  isLoading: boolean;
  error: any | null;
  message?: string | null;
}

const AUTH_IDENTITY_CHANGE_ACTIONS = new Set([
  'auth/login/fulfilled',
  'auth/loginWithVerificationCustomToken/fulfilled',
  'auth/register/fulfilled',
  'auth/providerAuth/fulfilled',
  'auth/logout/fulfilled',
]);

// Initial state
const initialState: PlanState = {
  plan: null,
  planDetails: null,
  planMembers: [],
  isActive: false,
  isLoading: false,
  error: null,
  message: null,
};

// Async Thunks
export const fetchPlan = createAsyncThunk(
  'plan/fetchPlan',
  async (planUid: string, { rejectWithValue }) => {
    try {
      const response = await planService.get(planUid);
      if (response) {
        return {
          plan: response.data,
        };
      }
    } catch (error: any) {
      return rejectWithValue(error.data?.error || error.message);
    }
  }
);

export const fetchPlanDetails = createAsyncThunk(
  'plan/fetchPlanDetails',
  async (plan: Plan, { rejectWithValue }) => {
    try {
      const response = await planService.getDetails(plan.uid);
      return transformSubscriptionToPlanDetails(response.data, plan.seats);
    } catch (error: any) {
      return rejectWithValue(error.data?.error || error.message);
    }
  }
);

export const fetchPlanMembers = createAsyncThunk(
  'plan/fetchPlanMembers',
  async (plan: Plan, { rejectWithValue }) => {
    try {
      const members = await planService.getMembers(plan.uid);
      const pendingInvites = await planService.pendingInvites(plan.uid);
      // const suggestedEmails = await planService.suggestedMembers(plan.uid);
      return {
        members: members.data,
        pendingInvites: pendingInvites.data,
        // suggestedEmails: suggestedEmails.data,
      };
    } catch (error: any) {
      return rejectWithValue(error.data?.error || error.message);
    }
  }
);

export const cancelPlan = createAsyncThunk(
  'plan/cancelPlan',
  async (plan: Plan, { rejectWithValue }) => {
    try {
      const response = await planService.cancel(plan.uid);

      if (!response.data?.data?.data) {
        return rejectWithValue({
          message: 'Invalid response data',
          status: response.status,
        });
      }

      const paddleData = response.data.data.data;
      return {
        planDetails: transformSubscriptionToPlanDetails(paddleData, plan.seats),
        status: response.status,
      };
    } catch (error: any) {
      return rejectWithValue({
        message: error.data?.error || error.message,
        status: error.status,
      });
    }
  }
);

export const undoCancelPlan = createAsyncThunk(
  'plan/undoCancelPlan',
  async (plan: Plan, { rejectWithValue }) => {
    try {
      const response = await paymentService.updateSubscription();
      const sub = await paymentService.getSubscription();

      if (!response.data || !sub.data) {
        throw new Error('Invalid response data');
      }

      return transformSubscriptionToPlanDetails(sub.data, plan.seats);
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to undo cancellation');
    }
  }
);

// Slice
const planSlice = createSlice({
  name: 'plan',
  initialState,
  reducers: {
    setPlan: (state, action: PayloadAction<Plan>) => {
      state.plan = action.payload;
      state.isActive =
        action.payload.status === PlanStatusEnum.ACTIVE ||
        action.payload.status === PlanStatusEnum.TRIALING;
    },
    setPlanLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setPlanDetails: (state, action: PayloadAction<PlanDetails>) => {
      state.planDetails = action.payload;
      state.isActive =
        action.payload.status === PlanStatusEnum.ACTIVE ||
        action.payload.status === PlanStatusEnum.TRIALING;
    },
    clearPlan: (state) => {
      state.plan = null;
      state.planDetails = null;
      state.isActive = false;
      state.isLoading = false;
      state.error = null;
      state.message = null;
    },
    clearPlanDetails: (state) => {
      state.planDetails = null;
    },
    clearPlanMembers: (state) => {
      state.planMembers = [];
    },
    clearMessageAndError(state) {
      state.isLoading = false;
      state.error = null;
      state.message = null;
    },
  },
  extraReducers: (builder) => {
    // fetchPlan
    builder.addCase(fetchPlan.pending, (state, action) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchPlan.fulfilled, (state, action) => {
      state.plan = action.payload?.plan ?? null;
      state.isActive =
        action.payload?.plan.status === PlanStatusEnum.ACTIVE ||
        action.payload?.plan.status === PlanStatusEnum.TRIALING;
      state.isLoading = false;
    });
    builder.addCase(fetchPlan.rejected, (state, action) => {
      state.isLoading = false;
      state.isActive = false;
      state.error = action.payload;
    });

    // fetchPlanDetails
    builder.addCase(fetchPlanDetails.pending, (state, action) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchPlanDetails.fulfilled, (state, action) => {
      state.planDetails = action.payload;
      state.isLoading = false;
      state.isActive =
        state.planDetails.status === PlanStatusEnum.ACTIVE ||
        state.planDetails.status === PlanStatusEnum.TRIALING;
    });
    builder.addCase(fetchPlanDetails.rejected, (state, action) => {
      state.isLoading = false;
      state.isActive = false;

      state.error = action.payload;
    });

    // fetchPlanMembers
    builder.addCase(fetchPlanMembers.pending, (state, action) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchPlanMembers.fulfilled, (state, action) => {
      state.planMembers = action.payload.members;
      if (state.plan) {
        state.plan.pendingInvites = action.payload.pendingInvites;
        // state.plan.suggestedEmails = action.payload.suggestedEmails;
      }
      state.isLoading = false;
    });
    builder.addCase(fetchPlanMembers.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    });

    // cancelPlan
    builder.addCase(cancelPlan.pending, (state, action) => {
      state.isLoading = true;
      state.error = null;
      state.message = null;
    });
    builder.addCase(cancelPlan.fulfilled, (state, action) => {
      state.planDetails = action.payload.planDetails;
      state.isActive =
        state.planDetails.status === PlanStatusEnum.ACTIVE ||
        state.planDetails.status === PlanStatusEnum.TRIALING;
      state.isLoading = false;
    });
    builder.addCase(cancelPlan.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    });

    // undoCancelPlan
    builder.addCase(undoCancelPlan.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(undoCancelPlan.fulfilled, (state, action) => {
      state.planDetails = action.payload;
      state.isLoading = false;
    });
    builder.addCase(undoCancelPlan.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.payload;
    });

    builder.addMatcher(
      (action): action is { type: string } =>
        AUTH_IDENTITY_CHANGE_ACTIONS.has(action.type),
      (state) => {
        state.plan = null;
        state.planDetails = null;
        state.planMembers = [];
        state.isActive = false;
        state.isLoading = false;
        state.error = null;
        state.message = null;
      }
    );
  },
});

// Actions
export const {
  setPlan,
  clearPlan,
  clearPlanDetails,
  clearPlanMembers,
  clearMessageAndError,
  setPlanDetails,
  setPlanLoading,
} = planSlice.actions;

// Selector
export const selectPlan = (state: { plan: PlanState }) => state.plan;

// Reducer
export default planSlice.reducer;
